import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { fmtDate, STATUS_KEYS } from "../lib/format";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Download, FileSpreadsheet, FileText, FileType2, Truck, BarChart3,
  Wallet, Scale, Filter, X, Search, ClipboardList, RotateCcw, Building2,
  Users, Timer, Target, Coins, Landmark, ShoppingCart, Camera, Shield,
} from "lucide-react";
import { toast } from "sonner";

const AREAS = [
  {
    key: "operation",
    icon: Truck,
    tone: "bg-[var(--brand-soft)] text-[var(--brand)]",
    titleKey: "report_area_operation",
    descKey: "report_area_operation_desc",
    reports: [
      { key: "operational_position", icon: ClipboardList, titleKey: "report_operational_position", descKey: "report_operational_position_desc", filters: ["period", "status", "deliverer", "search"] },
      { key: "occurrences", icon: ClipboardList, titleKey: "report_occurrences", descKey: "report_occurrences_desc", filters: ["period", "status", "deliverer"] },
      { key: "returns", icon: RotateCcw, titleKey: "report_returns", descKey: "report_returns_desc", filters: ["period"] },
    ],
  },
  {
    key: "management",
    icon: BarChart3,
    tone: "bg-[var(--info-soft)] text-[var(--brand)]",
    titleKey: "report_area_management",
    descKey: "report_area_management_desc",
    reports: [
      { key: "production", icon: Building2, titleKey: "report_production", descKey: "report_production_desc", filters: ["period"] },
      { key: "productivity", icon: Users, titleKey: "report_productivity", descKey: "report_productivity_desc", filters: ["period", "deliverer"] },
      { key: "sla", icon: Timer, titleKey: "report_sla", descKey: "report_sla_desc", filters: ["period"] },
    ],
  },
  {
    key: "finance",
    icon: Wallet,
    tone: "bg-[var(--success-soft)] text-[var(--success-text)]",
    titleKey: "report_area_finance",
    descKey: "report_area_finance_desc",
    reports: [
      { key: "commission_closing", icon: Coins, titleKey: "report_commission_closing", descKey: "report_commission_closing_desc", filters: ["period", "status", "deliverer"] },
      { key: "finance_aging", icon: Landmark, titleKey: "report_finance_aging", descKey: "report_finance_aging_desc", filters: ["period", "status"] },
      { key: "service_orders", icon: ShoppingCart, titleKey: "report_service_orders", descKey: "report_service_orders_desc", filters: ["period", "status", "deliverer"] },
    ],
  },
  {
    key: "legal",
    icon: Scale,
    tone: "bg-[var(--danger-soft)] text-[var(--danger)]",
    titleKey: "report_area_legal",
    descKey: "report_area_legal_desc",
    reports: [
      { key: "evidence_coverage", icon: Camera, titleKey: "report_evidence_coverage", descKey: "report_evidence_coverage_desc", filters: ["period", "deliverer"] },
      { key: "audit_trail", icon: Shield, titleKey: "report_audit_trail", descKey: "report_audit_trail_desc", filters: ["period", "search"] },
    ],
  },
];

const FORMATS = [
  { key: "csv",  label: "CSV",   icon: FileSpreadsheet, hint: "Compatível com Excel/Sheets" },
  { key: "xlsx", label: "Excel", icon: FileText,        hint: "Formatado, com filtros e cores" },
  { key: "pdf",  label: "PDF",   icon: FileType2,       hint: "Impressão / envio jurídico" },
];

const FINANCE_STATUSES = ["pending", "paid", "cancelled"];
const COMMISSION_STATUSES = ["pending", "paid"];
const OCCURRENCE_STATUSES = ["delivered", "attempted", "returned"];
const SERVICE_ORDER_STATUSES = ["pendente_aprovacao", "aprovada", "cancelada"];

export default function Reports() {
  const { t } = useI18n();
  const [selectedArea, setSelectedArea] = useState("operation");
  const [selectedReport, setSelectedReport] = useState(null);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(null);
  const [deliverers, setDeliverers] = useState([]);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    status: "",
    deliverer_id: "",
    q: "",
  });

  useEffect(() => {
    api.get("/users?role=deliverer").then(({ data }) => setDeliverers(data)).catch(() => {});
  }, []);

  const area = AREAS.find((a) => a.key === selectedArea);
  const report = area?.reports.find((r) => r.key === selectedReport) || null;
  const filterSet = report?.filters || [];

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  useEffect(() => {
    if (!selectedReport) return undefined;
    setCount(null);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/reports/${selectedReport}/count`, { params });
        setCount(typeof data.count === "number" ? data.count : 0);
      } catch {
        setCount(null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [selectedReport, params]);

  const statusOptions = useMemo(() => {
    if (!report) return STATUS_KEYS;
    if (report.key === "occurrences") return OCCURRENCE_STATUSES;
    if (report.key === "commission_closing") return COMMISSION_STATUSES;
    if (report.key === "finance_aging") return FINANCE_STATUSES;
    if (report.key === "service_orders") return SERVICE_ORDER_STATUSES;
    return STATUS_KEYS;
  }, [report]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const clearFilters = () => setFilters({ date_from: "", date_to: "", status: "", deliverer_id: "", q: "" });

  const selectArea = (key) => {
    setSelectedArea(key);
    setSelectedReport(null);
    setCount(null);
    clearFilters();
  };

  const download = async (fmt) => {
    if (!selectedReport) return;
    setBusy(fmt);
    try {
      const res = await api.get(`/reports/${selectedReport}.${fmt}`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nafe_${selectedReport}_${Date.now()}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Relatório ${fmt.toUpperCase()} baixado`);
    } catch {
      toast.error("Falha ao gerar relatório");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4">
        <div className="text-xs text-[var(--text-2)] font-medium">Exportação</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_reports")}</h1>
        <p className="text-xs text-[var(--text-2)] mt-1">{t("report_page_subtitle")}</p>
      </div>

      <div className="card-soft p-4">
        <div className="text-xs font-semibold text-[var(--text-2)] mb-3">{t("report_choose_area")}</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {AREAS.map((item) => {
            const Icon = item.icon;
            const active = selectedArea === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => selectArea(item.key)}
                className={`text-left p-4 rounded-2xl transition-all border ${
                  active
                    ? "border-[var(--brand)] bg-[var(--brand-soft)] shadow-sm"
                    : "border-transparent bg-[var(--surface-2)] hover:bg-[var(--surface-hover)]"
                }`}
                data-testid={`report-area-${item.key}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${item.tone}`}>
                  <Icon size={18} />
                </div>
                <div className={`text-sm font-bold ${active ? "text-[var(--brand)]" : "text-[var(--text-1)]"}`}>
                  {t(item.titleKey)}
                </div>
                <div className="text-[11px] text-[var(--text-2)] mt-1 leading-snug">
                  {t(item.descKey)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {area && (
        <div className="card-soft p-4">
          <div className="text-xs font-semibold text-[var(--text-2)] mb-3">{t("report_choose_report")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {area.reports.map((item) => {
              const Icon = item.icon;
              const active = selectedReport === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setSelectedReport(item.key);
                    setFilters((prev) => ({ ...prev, status: "" }));
                  }}
                  className={`text-left p-4 rounded-2xl transition-all border ${
                    active
                      ? "border-[var(--brand)] bg-[var(--brand-soft)] shadow-sm"
                      : "border-transparent bg-[var(--surface-2)] hover:bg-[var(--surface-hover)]"
                  }`}
                  data-testid={`report-select-${item.key}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${area.tone}`}>
                    <Icon size={16} />
                  </div>
                  <div className={`text-sm font-bold ${active ? "text-[var(--brand)]" : "text-[var(--text-1)]"}`}>
                    {t(item.titleKey)}
                  </div>
                  <div className="text-[11px] text-[var(--text-2)] mt-1 leading-snug">
                    {t(item.descKey)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {report && (
        <>
          <div className="card-soft p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
                  <Filter size={15} />
                </div>
                <div>
                  <div className="font-display text-base font-bold">Filtros</div>
                  <div className="text-[10px] text-[var(--text-2)]">Refine os dados antes de exportar</div>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-xs font-semibold text-[var(--text-2)] hover:text-[var(--danger)] inline-flex items-center gap-1" data-testid="clear-filters">
                  <X size={12} /> Limpar ({activeFilterCount})
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {filterSet.includes("period") && (
                <>
                  <div>
                    <label className="text-xs font-medium text-[var(--text-2)]">Data inicial</label>
                    <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="rounded-xl h-10 mt-1" data-testid="filter-date-from" />
                    <div className="text-[10px] text-[var(--text-3)] mt-1">Opcional. Vazio exporta tudo.</div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--text-2)]">Data final</label>
                    <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="rounded-xl h-10 mt-1" data-testid="filter-date-to" />
                  </div>
                </>
              )}

              {filterSet.includes("status") && (
                <div>
                  <label className="text-xs font-medium text-[var(--text-2)]">Status</label>
                  <Select value={filters.status || "__all__"} onValueChange={(v) => setFilters({ ...filters, status: v === "__all__" ? "" : v })}>
                    <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filterSet.includes("deliverer") && deliverers.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-[var(--text-2)]">Entregador</label>
                  <Select value={filters.deliverer_id || "__all__"} onValueChange={(v) => setFilters({ ...filters, deliverer_id: v === "__all__" ? "" : v })}>
                    <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="filter-deliverer"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {deliverers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filterSet.includes("search") && (
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-[var(--text-2)]">Busca</label>
                  <div className="relative mt-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                    <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Número, destinatário, CPF" className="rounded-xl h-10 pl-9" data-testid="filter-q" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card-soft p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="text-xs font-medium text-[var(--text-2)]">Pré-visualização</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="font-display text-4xl font-black tracking-tight text-[var(--text-1)]" data-testid="report-count">
                    {count === null ? "—" : count.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-[var(--text-2)]">
                    registro(s) em <span className="font-semibold text-[var(--text-1)]">{t(report.titleKey)}</span>
                  </div>
                </div>
                <div className="text-[10px] text-[var(--text-3)] mt-1 font-mono">
                  {t(area.titleKey)} · {fmtDate(new Date().toISOString())}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {FORMATS.map(({ key, label, icon: Icon, hint }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => download(key)}
                  disabled={busy !== null}
                  className="group text-left p-4 rounded-2xl bg-[var(--surface-2)] hover:bg-[var(--brand-soft)] disabled:opacity-50 disabled:cursor-not-allowed transition-all border border-transparent hover:border-[var(--brand)]"
                  data-testid={`download-${report.key}-${key}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-11 h-11 rounded-xl bg-white group-hover:bg-[var(--brand)] group-hover:text-white text-[var(--brand)] flex items-center justify-center transition-colors shadow-sm">
                      <Icon size={20} />
                    </div>
                    {busy === key ? (
                      <div className="w-5 h-5 rounded-full border-2 border-[var(--brand)] border-t-transparent animate-spin" />
                    ) : (
                      <Download size={16} className="text-[var(--text-3)] group-hover:text-[var(--brand)]" />
                    )}
                  </div>
                  <div className="font-display text-base font-bold text-[var(--text-1)]">{label}</div>
                  <div className="text-xs text-[var(--text-2)] mt-0.5">{hint}</div>
                </button>
              ))}
            </div>

            {count === 0 && (
              <div className="mt-4 rounded-xl bg-[var(--warning-soft)] text-[var(--warning-text)] px-3 py-2 text-xs">
                Nenhum registro encontrado com os filtros atuais.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
